import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface Organization {
  id: number;
  name: string;
}

interface CreateTeamData {
  organization_id: number;
  sport: string;
  gender: 'mixed' | 'male' | 'female';
}

interface CreateTeamModalProps {
  organizations: Organization[];
  onClose: () => void;
  onCreate: (teamData: CreateTeamData) => Promise<boolean>;
}

/**
 * Modal component for creating new teams
 */
const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ organizations, onClose, onCreate }) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState<CreateTeamData>({
    organization_id: 0,
    sport: '',
    gender: 'mixed'
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.organization_id || !formData.sport) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      const success = await onCreate(formData);
      if (success) {
        setSuccess('Team created successfully.');
        setTimeout(() => onClose(), 1200);
      } else {
        setError('Failed to create team. Please try again.');
      }
    } catch (error) {
      console.error('Error creating team:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while creating the team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
         <div className="fixed inset-0 bg-gray-800/80 dark:bg-gray-950/80 overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
                         <h3 className="text-2xl cgm-text-primary mb-2">
               Create New Team
             </h3>
            <p className="cgm-text-secondary text-sm">
              Add a new team to the system
            </p>
          </div>
          {error && (
            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
          )}
          {success && (
            <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">{success}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="organization" className="block text-sm font-medium cgm-text-secondary mb-1">
                Organization
              </label>
              <select
                id="organization"
                required
                value={formData.organization_id}
                onChange={(e) => setFormData({...formData, organization_id: parseInt(e.target.value)})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value={0}>Select an organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="sport" className="block text-sm font-medium cgm-text-secondary mb-1">
                Sport
              </label>
              <input
                id="sport"
                type="text"
                required
                value={formData.sport}
                onChange={(e) => setFormData({...formData, sport: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter sport name"
              />
            </div>
            
            <div>
              <label htmlFor="gender" className="block text-sm font-medium cgm-text-secondary mb-1">
                Gender
              </label>
              <select
                id="gender"
                value={formData.gender}
                onChange={(e) => setFormData({...formData, gender: e.target.value as 'mixed' | 'male' | 'female'})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="mixed">Mixed</option>
                <option value="male">Men's</option>
                <option value="female">Women's</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                  Creating Team...
                </>
              ) : (
                'Create Team'
              )}
            </button>
          </form>

          {/* Navigation Links */}
          <div className="mt-6 space-y-4 text-center">
                         <button
               onClick={onClose}
               className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
             >
               Cancel
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTeamModal; 